import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Filters from './Filters';
import SkeletonCuts from './SkeletonCuts';
import CutsList from './CutsList';
import PrimalCutsGraphic from './PrimalCutsGraphic';
import CutsHeader from './CutsHeader';
import { AJAX_URL } from '../../config/index.js';
import useDisableQuery from './useDisableQuery';

export default function Cuts() {
  const [isLoading, setLoading] = useState(true);
  const [primalCuts, setPrimalCuts] = useState([]);
  const [cookingMethods, setCookingMethods] = useState([]);
  const hadActiveFilters = useRef(false);
  const [query, setQuery] = useState({
    primalCutsQueryIDs: [],
    cookingMethodsQueryIDs: []
  });
  const [cutsList, setCutsList] = useState([]);
  const [disableQuery, releaseDisableQuery] = useDisableQuery();

  useEffect(() => {
    let isSubscribed = true;
    setLoading(true);

    const url = `${AJAX_URL}post_wlQueryCuts`;

    axios
      .post(url, {})
      .then((res) => {
        if (isSubscribed) {
          setPrimalCuts(res.data.data.primalCuts);
          setCookingMethods(res.data.data.cookingMethods);
          setCutsList(res.data.data.cuts);
          releaseDisableQuery();
        }
      })
      .catch((err) => console.log(err.message));

    return () => (isSubscribed = false);
  }, []);

  // follow up axios call for updated query params
  useEffect(() => {
    if (disableQuery) return;

    const hasActiveFilters =
      query.primalCutsQueryIDs.length > 0 ||
      query.cookingMethodsQueryIDs.length > 0;

    if (!hasActiveFilters && !hadActiveFilters.current) return;

    hadActiveFilters.current = hasActiveFilters;

    let isSubscribed = true;

    const url = `${AJAX_URL}post_wlQueryCuts`;

    const payload = new FormData();
    payload.append(
      'primalCutsQueryIDs',
      JSON.stringify(query.primalCutsQueryIDs)
    );
    payload.append(
      'cookingMethodsQueryIDs',
      JSON.stringify(query.cookingMethodsQueryIDs)
    );

    setLoading(true);

    axios
      .post(url, payload)
      .then((res) => {
        if (isSubscribed) {
          setCutsList(res.data.data.cuts);
          setLoading(false); // Make sure to set loading to false after updating the state
        }
      })
      .catch((err) => {
        if (isSubscribed) {
          setLoading(false); // Also set loading to false in case of an error
        }
      });

    return () => {
      isSubscribed = false;
    };
  }, [query, disableQuery]); // Include disableQuery in the dependency array

  // update query logic: if loading ignore, else find all selected item ids and add to query payload
  useEffect(() => {
    if (isLoading) return;

    const updatedPrimalCutsQueryIDs = [];
    const updatedCookingMethodsQueryIDs = [];

    primalCuts.forEach((item) => {
      if (item.selected === true)
        return updatedPrimalCutsQueryIDs.push(item.id);
    });

    cookingMethods.forEach((item) => {
      if (item.selected === true) {
        return updatedCookingMethodsQueryIDs.push(item.id);
      }
    });

    setQuery({
      primalCutsQueryIDs: updatedPrimalCutsQueryIDs,
      cookingMethodsQueryIDs: updatedCookingMethodsQueryIDs
    });
  }, [cookingMethods, primalCuts]);

  // set loading state based on variable existence
  useEffect(() => {
    cutsList && cookingMethods.length > 0 && primalCuts.length > 0
      ? setLoading(false)
      : null;
  }, [cutsList, cookingMethods, primalCuts]);

  return (
    <>
      <CutsHeader>
        <PrimalCutsGraphic setPrimalCuts={setPrimalCuts} />
      </CutsHeader>
      <Filters
        primalCuts={primalCuts}
        cookingMethods={cookingMethods}
        setPrimalCuts={setPrimalCuts}
        setCookingMethods={setCookingMethods}
      />
      {!isLoading ? (
        cutsList.length > 0 ? (
          <CutsList cutsList={cutsList} />
        ) : (
          <div style={{ textAlign: 'center', marginBottom: '50px' }}>
            No results found.
          </div>
        )
      ) : (
        <SkeletonCuts />
      )}
    </>
  );
}
